import React, { useEffect, useMemo, useRef } from 'react'

export type OfferMenuAction = 'view' | 'edit' | 'close' | 'archive' | 'clone' | 'delete'

export interface OfferMenuItem {
  action: OfferMenuAction
  label: string
  tone?: 'default' | 'danger'
}

interface OffersTableRowMenuProps {
  anchorRect: DOMRect
  items: OfferMenuItem[]
  onClose: () => void
  onAction: (action: OfferMenuAction) => void
}

const MENU_WIDTH = 170
const MENU_ITEM_HEIGHT = 21

export function OffersTableRowMenu({
  anchorRect,
  items,
  onClose,
  onAction,
}: OffersTableRowMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  const style = useMemo(() => {
    const separatorCount = Math.max(0, items.length - 1)
    const menuHeight = items.length * MENU_ITEM_HEIGHT + separatorCount * 12 + 24
    const gap = 8

    let left = anchorRect.right - MENU_WIDTH
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8))

    let top = anchorRect.bottom + gap
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, anchorRect.top - menuHeight - gap)
    }

    return { left, top }
  }, [anchorRect, items.length])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onMouseDown = (e: MouseEvent) => {
      const el = menuRef.current
      if (!el) return
      if (!el.contains(e.target as Node)) onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [onClose])

  if (items.length === 0) return null

  return (
    <div
      ref={menuRef}
      className="fixed z-[80] w-[170px] rounded-[10px] border border-[#D8D8D8] bg-white py-3 dark:border-border dark:bg-card"
      style={{ left: style.left, top: style.top }}
      role="menu"
    >
      <div className="flex flex-col">
        {items.map((item, idx) => (
          <React.Fragment key={item.action}>
            <button
              type="button"
              className={`w-[170px] h-[21px] px-4 flex items-center justify-center text-center text-[14px] leading-[150%] font-medium ${
                item.tone === 'danger'
                  ? 'text-[#E00000] dark:text-red-400'
                  : 'text-[#3F3F3F] dark:text-foreground'
              }`}
              onClick={() => {
                onAction(item.action)
                onClose()
              }}
              role="menuitem"
            >
              {item.label}
            </button>
            {idx < items.length - 1 && (
              <div className="w-[170px] border-t border-[#D8D8D8] my-1.5 dark:border-border" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
